'use client';

import styles from './InlineUnsafe.module.css';
import { convertEscapedHTML } from './InlineUnsafe.utils';
import { getClasses } from '@/_utils/classnames';
import { sanitizeText } from '@/_utils/sanitize';
import { createElement, useMemo } from 'react';
import type { FC, ReactNode, CSSProperties } from 'react';
import type { InlineUnsafeProps } from './InlineUnsafe.types';
import { Tooltip } from '@/_components';

// Escape regex special characters
const escapeRegex = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const InlineUnsafe: FC<InlineUnsafeProps> = ({
  isExtremelyUnsafe,
  glossaries = {},
  text,
}) => {
  const glossaryEntries = useMemo(
    () => Object.entries(glossaries),
    [glossaries],
  );

  const hasGlossaries: boolean = useMemo(
    () => (glossaries ? glossaryEntries?.length > 0 : false),
    [glossaries, glossaryEntries],
  );

  const processedContent = useMemo(() => {
    if (!text) return null;

    const safeText: string = isExtremelyUnsafe
      ? convertEscapedHTML(text)
      : sanitizeText(text);

    const inlineUnsafeClasses: string = getClasses(styles.inlineUnsafe, {
      [styles.inlineUnsafeExtremely]: isExtremelyUnsafe,
    });

    // If no glossaries, just render the text safely
    if (!hasGlossaries) {
      return (
        <div
          data-component="InlineUnsafe"
          className={inlineUnsafeClasses}
          dangerouslySetInnerHTML={{ __html: safeText }}
        />
      );
    }

    // Parse text and replace glossary terms with Tooltip components
    const renderTextWithGlossaries = (textContent: string) => {
      // Parse HTML string into DOM tree
      const parser = new DOMParser();
      const doc = parser.parseFromString(
        `<div>${textContent}</div>`,
        'text/html',
      );
      const container = doc.body.firstChild as HTMLElement;

      // Recursive function to process DOM nodes and convert to React elements
      const processNode = (node: Node, index: number): ReactNode => {
        if (node.nodeType === Node.TEXT_NODE) {
          // Process text node for glossary terms
          const textValue = node.textContent || '';
          return processTextForGlossary(textValue, index);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Process element node - recreate as React element
          const element = node as HTMLElement;
          const tagName = element.tagName.toLowerCase();

          // Get all attributes and convert to React props
          const props: { [key: string]: string | CSSProperties } = {};
          for (let i = 0; i < element.attributes.length; i++) {
            const attr = element.attributes[i];

            let propName = attr.name;
            let propValue: string | CSSProperties = attr.value;

            if (propName === 'class') {
              propName = 'className';
            } else if (propName === 'style' && typeof propValue === 'string') {
              // Convert CSS string to React style object
              propValue = propValue
                .split(';')
                .reduce((styleObj: { [key: string]: string }, rule: string) => {
                  const [property, value] = rule
                    .split(':')
                    .map((str) => str.trim());
                  if (property && value) {
                    // Convert kebab-case (CSS) to camelCase (React) (e.g., 'margin-top' to 'marginTop')
                    const camelProperty = property.replace(
                      /-([a-z])/g,
                      (_, letter) => letter.toUpperCase(),
                    );
                    styleObj[camelProperty] = value;
                  }
                  return styleObj;
                }, {});
            }

            props[propName] = propValue;
          }

          // Recursively process children
          const children = Array.from(element.childNodes).map(
            (child, childIndex) => processNode(child, childIndex),
          );

          return createElement(tagName, { key: index, ...props }, ...children);
        }

        return null;
      };

      // Helper function to process text content for glossary terms
      const processTextForGlossary = (text: string, baseIndex: number) => {
        if (!text) return [];

        const glossaryMatches: Array<{
          term: string;
          definition: string;
          start: number;
          end: number;
        }> = [];

        // Find all glossary terms in this text
        glossaryEntries.forEach(([term, definition]) => {
          const escapedTerm = escapeRegex(term);
          const regex = new RegExp(`\\b(${escapedTerm})\\b`, 'gi');

          let match;
          while ((match = regex.exec(text)) !== null) {
            glossaryMatches.push({
              term: match[1],
              definition,
              start: match.index,
              end: match.index + match[1].length,
            });
          }
        });

        // Sort matches by position
        glossaryMatches.sort((a, b) => a.start - b.start);

        // If no matches, return plain text
        if (glossaryMatches.length === 0) {
          return [<span key={baseIndex}>{text}</span>];
        }

        // Split text by glossary matches
        const elements: React.ReactNode[] = [];
        let lastIndex = 0;

        glossaryMatches.forEach((match, matchIndex) => {
          // Add text before the match
          if (match.start > lastIndex) {
            elements.push(
              <span key={`${baseIndex}-text-${matchIndex}`}>
                {text.slice(lastIndex, match.start)}
              </span>,
            );
          }

          // Add glossary term with tooltip
          elements.push(
            <Tooltip
              key={`${baseIndex}-glossary-${matchIndex}`}
              variant="glossary"
              placement="bottom-start"
            >
              <Tooltip.Trigger>
                <span className={styles.inlineUnsafe__glossaryTerm}>
                  {match.term}
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content>
                <div dangerouslySetInnerHTML={{ __html: match.definition }} />
              </Tooltip.Content>
            </Tooltip>,
          );

          lastIndex = match.end;
        });

        // Add remaining text
        if (lastIndex < text.length) {
          elements.push(
            <span key={`${baseIndex}-text-end`}>{text.slice(lastIndex)}</span>,
          );
        }

        return elements;
      };

      // Process all child nodes of the container
      const processedElements = Array.from(container.childNodes).map(
        (node, index) => processNode(node, index),
      );

      return processedElements.flat();
    };

    return (
      <div data-component="InlineUnsafe" className={inlineUnsafeClasses}>
        {renderTextWithGlossaries(safeText)}
      </div>
    );
  }, [text, isExtremelyUnsafe, hasGlossaries, glossaryEntries]);

  return processedContent;
};
